import { FC, useState } from 'react';
import { format, formatDistanceToNow } from 'date-fns';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { RefreshCw, Plus, X, Globe } from 'lucide-react';
import { toast } from 'sonner';
import { useSiteDiscovery, useUpdateSiteDiscovery } from '@/hooks/useBot';
import { extractErrorMessage } from '@/utils/errorHandling';
import type { ISiteDiscoveryHost, SiteDiscoveryHostSource } from '@/types/links';
import SectionCard from './shared/SectionCard';
import ToggleRow from './shared/ToggleRow';
import InfoBanner from './shared/InfoBanner';

const SOURCE_LABEL: Record<SiteDiscoveryHostSource, string> = {
  primary: 'Main site',
  configured: 'Added by you',
  linked: 'Found in your links',
};

const SOURCE_VARIANT: Record<SiteDiscoveryHostSource, 'neutral' | 'info' | 'secondary'> = {
  primary: 'neutral',
  configured: 'info',
  linked: 'secondary',
};

/** A date the customer can act on, or null to render nothing. */
const when = (value: string | null): string | null => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : format(date, 'd MMM yyyy');
};

const relative = (value: string | null): string | null => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return formatDistanceToNow(date, { addSuffix: true });
};

/**
 * Weekly page discovery, as a setting the workspace owns (mb-server#6436).
 *
 * The job lists the site once a week, compares it against what it listed last
 * time, and adds genuinely new pages to the bot. Three things have to be
 * visible for that not to feel like something happening to the customer:
 * whether it is on, which hosts get read and why each one is in the set, and
 * what the last run actually did. A bare switch over invisible behaviour is
 * what this replaces.
 *
 * Every write is a patch, so a row toggle cannot clobber the host list and the
 * switch cannot clobber either.
 */
const SiteDiscovery: FC = () => {
  const { data, isLoading, isError, refetch } = useSiteDiscovery();
  const { mutateAsync, isPending } = useUpdateSiteDiscovery();
  const [newHost, setNewHost] = useState('');
  const [hostError, setHostError] = useState('');

  const save = async (patch: Parameters<typeof mutateAsync>[0], success: string) => {
    try {
      await mutateAsync(patch);
      setHostError('');
      toast.success(success);
      return true;
    } catch (error) {
      // A rejected host comes back with the server's own wording — which host
      // and why. Showing "something went wrong" instead would leave the
      // customer with no way to correct it.
      const message = extractErrorMessage(error, 'Could not save page discovery settings');
      setHostError(message);
      toast.error(message);
      return false;
    }
  };

  if (isLoading) {
    return (
      <SectionCard icon={RefreshCw} title="Automatic page discovery">
        <Skeleton className="h-5 w-72" />
        <Skeleton className="h-16 w-full" />
      </SectionCard>
    );
  }

  if (isError || !data) {
    return (
      <SectionCard icon={RefreshCw} title="Automatic page discovery">
        <InfoBanner variant="warning" title="Settings unavailable">
          We could not load your page discovery settings.
          {' '}
          <button type="button" className="underline" onClick={() => refetch()}>Try again</button>
        </InfoBanner>
      </SectionCard>
    );
  }

  const {
    enabled, primaryHost, hosts, extraHosts, excludedHosts,
    maxExtraHosts, lastRunAt, nextRunAt, lastStatus, lastError, lastSummary,
  } = data;

  const canAddHost = extraHosts.length < maxExtraHosts;

  const addHost = async () => {
    const value = newHost.trim();
    if (!value) return;
    const ok = await save(
      { extraHosts: [...extraHosts, value] },
      `${value} will be read on the next run`,
    );
    if (ok) setNewHost('');
  };

  const removeHost = (host: string) => save(
    { extraHosts: extraHosts.filter((h) => h !== host) },
    `${host} removed`,
  );

  const setHostIncluded = (host: ISiteDiscoveryHost, included: boolean) => save(
    {
      excludedHosts: included
        ? excludedHosts.filter((h) => h !== host.host)
        : [...excludedHosts, host.host],
    },
    included ? `${host.host} will be read again` : `${host.host} will be left alone`,
  );

  const admitted = lastSummary?.admitted ?? 0;
  const lastRun = relative(lastRunAt);
  const nextRun = when(nextRunAt);

  // Every run outcome the job can store, said in terms the customer can act
  // on. Collapsing them into "did not finish / did finish" is how a state that
  // needs a person (bulk_change repeats every week until someone looks) reads
  // as a transient blip, and how a cost ceiling reads as a bug on our side.
  const outcome = ((): { title: string; body: string; variant: 'info' | 'warning'; showNext: boolean } | null => {
    if (!lastStatus || !lastRunAt) {
      return {
        title: 'Not checked yet',
        // Before the first run there is no baseline, so nothing has been
        // admitted — "0 new pages" would read as a failure rather than as the
        // run recording what the site looks like today.
        body: 'The first check records what your site looks like today; pages published after it are what get added.',
        variant: 'info',
        showNext: true,
      };
    }
    const checked = `Last checked ${lastRun}`;
    switch (lastStatus) {
      case 'baseline':
        return {
          title: checked,
          body: 'We recorded what your site looks like today. Pages published from now on are what get added.',
          variant: 'info',
          showNext: true,
        };
      case 'ok':
        return {
          title: checked,
          body: admitted > 0
            ? `${admitted} new page${admitted === 1 ? '' : 's'} added.`
            : 'No new pages since the last check.',
          variant: 'info',
          showNext: true,
        };
      case 'bulk_change':
        return {
          title: 'Your site changed more than we add automatically',
          // This repeats every week until the pages are added by hand or the
          // host is excluded, so it must not say "we will try again".
          body: `We found ${lastSummary?.new ?? 'many'} pages your bot has not seen — too many to be a week of publishing, so we did not add them. Add the ones you want under Website Links, or leave out a subdomain you do not need.`,
          variant: 'warning',
          showNext: false,
        };
      case 'budget_exhausted':
        return {
          title: 'Paused for this month',
          body: 'This workspace reached its monthly page-discovery allowance. Checks resume next month, and nothing already added is affected.',
          variant: 'warning',
          showNext: false,
        };
      case 'empty':
        return {
          title: 'We could not read any pages from your site',
          body: `Nothing came back for ${primaryHost}. If the site is behind a login or blocks crawlers, discovery cannot see it.`,
          variant: 'warning',
          showNext: true,
        };
      default:
        return {
          title: 'The last check did not finish',
          body: lastError || 'We will try again on the next check.',
          variant: 'warning',
          showNext: true,
        };
    }
  })();

  return (
    <SectionCard
      icon={RefreshCw}
      title="Automatic page discovery"
      subtitle="Once a week we check your site for pages your bot has not seen yet, and add the ones that are real content."
    >
      <div className="divide-y divide-gray-100">
        <ToggleRow
          className="pt-0"
          label="Keep this bot up to date automatically"
          description={
            primaryHost
              ? `We list ${primaryHost} weekly and only add pages that are new since the last check. Nothing is removed.`
              : 'Set your website URL in workspace settings first — there is no site to check yet.'
          }
          checked={enabled}
          disabled={isPending || !primaryHost}
          onCheckedChange={(value) => save(
            { enabled: value },
            value ? 'Page discovery is on' : 'Page discovery is off',
          )}
        />
      </div>

      {enabled && primaryHost && (
        <>
          {outcome ? (
            <InfoBanner variant={outcome.variant} title={outcome.title}>
              {outcome.body}
              {nextRun && outcome.showNext ? ` Next check ${nextRun}.` : ''}
            </InfoBanner>
          ) : null}

          <div>
            <p className="text-sm font-medium text-gray-900">Where we look</p>
            <p className="mt-0.5 text-[13px] leading-relaxed text-gray-500">
              Your main site, plus any of your own subdomains. Turn one off to leave it alone.
            </p>

            <ul className="mt-3 divide-y divide-gray-100 rounded-lg border border-gray-200">
              {hosts.map((host) => (
                <li key={host.host} className="flex items-center gap-3 px-4 py-3">
                  <Globe className="h-4 w-4 shrink-0 text-gray-400" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-gray-900">{host.host}</p>
                    <p className="mt-0.5 text-xs text-gray-500">
                      {host.pages > 0
                        ? `${host.pages} page${host.pages === 1 ? '' : 's'} in your bot`
                        : 'No pages yet'}
                    </p>
                  </div>
                  <Badge variant={SOURCE_VARIANT[host.source]} size="sm" className="shrink-0">
                    {SOURCE_LABEL[host.source]}
                  </Badge>
                  {host.source === 'configured' && (
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={`Remove ${host.host}`}
                      className="h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive"
                      disabled={isPending}
                      onClick={() => removeHost(host.host)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                  <Switch
                    checked={!host.excluded}
                    aria-label={`Check ${host.host}`}
                    // The main site is what the feature reads; switching the
                    // whole feature off is how you ask for nothing.
                    disabled={isPending || host.source === 'primary'}
                    onCheckedChange={(value) => setHostIncluded(host, value)}
                    className="shrink-0"
                  />
                </li>
              ))}
            </ul>

            <div className="mt-3 flex items-start gap-2">
              <div className="flex-1">
                <Input
                  placeholder={`docs.${primaryHost}`}
                  value={newHost}
                  disabled={!canAddHost || isPending}
                  onChange={(e) => {
                    setNewHost(e.target.value);
                    setHostError('');
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') addHost();
                  }}
                />
                {hostError && <p className="mt-1.5 text-[13px] text-destructive">{hostError}</p>}
                {!hostError && !canAddHost && (
                  <p className="mt-1.5 text-[13px] text-gray-500">
                    {`You can add up to ${maxExtraHosts} subdomains. Remove one to add another.`}
                  </p>
                )}
                {!hostError && canAddHost && (
                  <p className="mt-1.5 text-[13px] text-gray-500">
                    {`Must be ${primaryHost} or one of its subdomains.`}
                  </p>
                )}
              </div>
              <Button
                variant="outline"
                disabled={!canAddHost || isPending || !newHost.trim()}
                onClick={addHost}
              >
                <Plus className="mr-1.5 h-4 w-4" />
                Add
              </Button>
            </div>
          </div>
        </>
      )}
    </SectionCard>
  );
};

export default SiteDiscovery;
